import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import {
  AutocompleteContainer,
  AutocompleteInput,
  AutocompleteSuggestBody,
  AutocompleteValue,
  InputDate,
  InputDateContainer,
  InputDateOpener,
  InputSelectBody,
  InputSelectContainer,
  InputSelectContainerOptions,
  InputSelectValueOption,
  InputText,
  InputTextArea,
} from "./input-v2.style";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { ToMoneyFormat, debounce } from "../../../utils/Helper";

export interface IOptions {
  text: string;
  value: any;
}

const Input = () => {};

// This will be wrapper of React Form Component
interface InputControllerProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  rules?:
    | Omit<
        RegisterOptions<T, Path<T>>,
        "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
      >
    | undefined;
  render(
    field: ControllerRenderProps<T, Path<T>>,
    fieldState: ControllerFieldState
  ): React.ReactElement | React.ReactElement[];
}

Input.Controller = <T extends FieldValues>({
  control,
  name,
  rules,
  render,
}: InputControllerProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <React.Fragment>{render(field, fieldState)}</React.Fragment>
      )}
    />
  );
};

Input.Money = ({
  value,
  onChange,
  label,
  disabled,
  isFetching,
  error,
}: {
  label: string;
  value?: number | string;
  disabled?: boolean;
  isFetching?: boolean;
  error?: any;
  onChange(value: number): void;
}) => {
  const [amount, setAmount] = useState<string>("");

  useEffect(() => {
    if (value) {
      const target = value.toString().replaceAll(".", "");
      const temp = ToMoneyFormat(target);
      setAmount(temp || "");
    } else {
      //setAmount("");
    }
  }, [value]);

  function handleOnChage(e: string) {
    const target = e.replaceAll(".", "");
    const temp = ToMoneyFormat(target);
    setAmount(temp || "");
    onChange(target ? parseInt(target) : 0);
  }

  return (
    <Box width={"100%"}>
      <Box mb={0.5}>
        <Typography variant="body2">{label}</Typography>
      </Box>
      {!isFetching ? (
        <InputText
          value={amount}
          disabled={disabled}
          onChange={(e) => handleOnChage(e.target.value)}
        />
      ) : (
        <Skeleton style={{ height: 40 }} animation="wave" />
      )}
      {error && (
        <Box ml={1}>
          <Typography color={"red"} variant="caption">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

Input.Text = ({
  disable,
  type,
  value,
  label,
  placeholder,
  isFetching,
  error,
  showAsStatic,
  onChange,
}: {
  disable?: boolean;
  type?: string;
  value?: any;
  label: string;
  isFetching?: boolean;
  error?: any;
  placeholder?: string;
  showAsStatic?: boolean;
  onChange?(value: string): void;
}) => {
  if (showAsStatic) {
    return (
      <Box width={"100%"}>
        <Box mb={0.5}>
          <Typography variant="body1">{label}</Typography>
        </Box>
        <Box>
          <Typography fontWeight={"bold"}>{value || "-"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={"100%"}>
      <Box mb={0.5}>
        <Typography variant="body2">{label}</Typography>
      </Box>
      {!isFetching ? (
        <InputText
          disabled={disable}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange && onChange(e.target.value)}
          onWheel={(e: any) => e.target.blur()}
        />
      ) : (
        <Skeleton style={{ height: 40 }} animation="wave" />
      )}
      {error && (
        <Box ml={1}>
          <Typography color={"red"} variant="caption">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

Input.TextArea = ({
  row,
  value,
  label,
  placeholder,
  isFetching,
  error,
  disable,
  showAsStatic,
  onChange,
}: {
  row?: number;
  value?: any;
  label: string;
  isFetching?: boolean;
  error?: any;
  disable?: boolean;
  placeholder?: string;
  showAsStatic?: boolean;
  onChange?(value: string): void;
}) => {
  if (showAsStatic) {
    return (
      <Box width={"100%"}>
        <Box mb={0.5}>
          <Typography variant="body2">{label}</Typography>
        </Box>
        <Box>
          <Typography fontWeight={"bold"}>{value || "-"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={"100%"}>
      <Box mb={0.5}>
        <Typography variant="body2">{label}</Typography>
      </Box>
      {!isFetching ? (
        <InputTextArea
          rows={row}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange && onChange(e.target.value)}
          disabled={disable}
        />
      ) : (
        <Skeleton style={{ height: 80 }} animation="wave" />
      )}
      {error && (
        <Box ml={1}>
          <Typography color={"red"} variant="caption">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

Input.Select = ({
  label,
  options,
  value,
  isFetching,
  error,
  disable,
  onChange,
  showAsStatic,
}: {
  label: string;
  options: IOptions[];
  value: any;
  isFetching?: boolean;
  disable?: boolean;
  error?: any;
  showAsStatic?: boolean;
  onChange(item: IOptions): void;
}) => {
  const [showOption, setShowOption] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>();
  const shouldShowOptionsRef = useRef(showOption);

  function handleClose() {
    if (shouldShowOptionsRef.current) {
      setShowOption(false);
    }
  }

  const handleOnChange = (item: IOptions) => {
    if (!disable) {
      setSelectedValue(item.text);
      setShowOption(false);
      onChange(item);
    }
  };

  useEffect(() => {
    window.addEventListener("click", handleClose);
    return () => {
      window.removeEventListener("click", handleClose);
    };
  }, []);

  useEffect(() => {
    shouldShowOptionsRef.current = showOption;
  }, [showOption]);

  useEffect(() => {
    if (value && options.length > 0) {
      let targetValue = value;
      if (typeof value === "object") {
        targetValue = value.value;
      }
      const target = options.find(
        (item) => item.value.toString() === targetValue.toString()
      );
      if (target) {
        setSelectedValue(target.text);
      }
    }
  }, [isFetching, value, options]);

  if (showAsStatic) {
    return (
      <Box width={"100%"}>
        <Box mb={0.5}>
          <Typography variant="body2">{label}</Typography>
        </Box>
        <Box>
          <Typography fontWeight={"bold"}>{selectedValue || "-"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box position={"relative"} onClick={(e) => e.stopPropagation()}>
      <InputSelectContainer onClick={() => !disable && setShowOption(true)}>
        {!isFetching && !disable && (
          <Box position={"absolute"} top={"50%"} right={"2%"}>
            <ExpandMoreIcon />
          </Box>
        )}
        <Box mb={0.5}>
          <Typography variant="body2">{label}</Typography>
        </Box>
        {!isFetching && options.length > 0 ? (
          <InputSelectBody disabled={disable}>
            <Box>{selectedValue}</Box>
          </InputSelectBody>
        ) : (
          <Skeleton style={{ height: 40 }} animation="wave" />
        )}
      </InputSelectContainer>
      <InputSelectContainerOptions show={showOption}>
        {options.map((item, index) => {
          return (
            <InputSelectValueOption
              key={index}
              onClick={() => handleOnChange(item)}
            >
              {item.text}
            </InputSelectValueOption>
          );
        })}
      </InputSelectContainerOptions>
      {error && (
        <Box ml={1}>
          <Typography color={"red"} variant="caption">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

Input.Autocomplete = ({
  label,
  onChange,
  onClick,
  options,
  disable,
  isFetching,
  showAsStatic,
  value,
  error,
}: {
  label: string;
  options: IOptions[];
  showAsStatic?: boolean;
  isFetching?: boolean;
  disable?: boolean;
  error?: any;
  value?: any;
  onChange(value: string): void;
  onClick(value: IOptions): void;
}) => {
  const [isFirstRender, setIsFirstRender] = useState(false);
  const [shouldShowOptions, setShouldShowOptions] = useState(false);
  const shouldShowOptionsRef = useRef(shouldShowOptions);
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);

  function handleClose() {
    if (shouldShowOptionsRef.current) {
      setShouldShowOptions(false);
    }
  }

  useEffect(() => {
    window.addEventListener("click", handleClose);
    return () => {
      window.removeEventListener("click", handleClose);
    };
  }, []);

  useEffect(() => {
    shouldShowOptionsRef.current = shouldShowOptions;
  }, [shouldShowOptions]);

  useEffect(() => {
    if (options && value && inputValue === undefined) {
      setIsFirstRender(true);
      const temp = options.find((item) => {
        if (typeof value === "object") {
          return item.value === value.value;
        }
        return item.value === value;
      });
      if (temp) {
        setInputValue(temp.text);
      } else {
        setInputValue(value.label);
      }
    }
  }, [options, value]);

  const handleOnKeyUp = debounce((value: string) => {
    onChange(value);
  }, 250);

  const handleOnChange = (item: IOptions) => {
    setShouldShowOptions(false);
    onClick(item);
    setInputValue(item.text);
  };

  if (showAsStatic) {
    return (
      <Box width={"100%"}>
        <Box mb={0.5}>
          <Typography variant="body2">{label}</Typography>
        </Box>
        <Box>
          <Typography fontWeight={"bold"}>{inputValue || "-"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={"100%"} onClick={(e) => e.stopPropagation()}>
      <Box mb={0.5}>
        <Typography variant="body2">{label}</Typography>
      </Box>
      <AutocompleteContainer>
        {isFetching && options.length <= 0 && !isFirstRender ? (
          <Skeleton style={{ height: 40 }} animation="wave" />
        ) : (
          <AutocompleteInput
            disabled={disable}
            onFocus={() => setShouldShowOptions(true)}
            onChange={(e) => {
              setInputValue(e.target.value);
              handleOnKeyUp(e.target.value);
            }}
            value={inputValue}
          />
        )}

        {error && (
          <Box ml={1}>
            <Typography color={"red"} variant="caption">
              {error}
            </Typography>
          </Box>
        )}
        <AutocompleteSuggestBody show={shouldShowOptions}>
          {!isFetching &&
            options.map((item, index) => {
              return (
                <AutocompleteValue
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOnChange(item);
                  }}
                >
                  {item.text}
                </AutocompleteValue>
              );
            })}
          {isFetching && (
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CircularProgress size={20} />
            </Box>
          )}
          {options.length <= 0 && !isFetching && (
            <AutocompleteValue style={{ cursor: "none" }}>
              Not Found
            </AutocompleteValue>
          )}
        </AutocompleteSuggestBody>
      </AutocompleteContainer>
    </Box>
  );
};

Input.Date = ({
  disable,
  value,
  label,
  isFetching,
  error,
  showAsStatic,
  onChange,
}: {
  disable?: boolean;
  value?: any;
  label: string;
  isFetching?: boolean;
  error?: any;
  showAsStatic?: boolean;
  onChange?(value: string): void;
}) => {
  const ref = useRef<any>();

  const handleOpenInputDate = () => {
    if (ref.current) {
      ref.current.showPicker();
    }
  };

  if (showAsStatic) {
    return (
      <Box width={"100%"}>
        <Box mb={0.5}>
          <Typography variant="body2">{label}</Typography>
        </Box>
        <Box>
          <Typography fontWeight={"bold"}>{value || "-"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={"100%"}>
      <Box mb={0.5}>
        <Typography variant="body2">{label}</Typography>
      </Box>
      {!isFetching ? (
        <InputDateContainer>
          <InputDate
            ref={ref}
            type="date"
            value={value}
            onChange={(e) => onChange && onChange(e.target.value)}
          />
          <InputDateOpener>
            <CalendarMonthIcon onClick={handleOpenInputDate} />
          </InputDateOpener>
        </InputDateContainer>
      ) : (
        <Skeleton style={{ height: 40 }} animation="wave" />
      )}
      {error && (
        <Box ml={1}>
          <Typography color={"red"} variant="caption">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Input;
